import type Anthropic from '@anthropic-ai/sdk';
import type {
  LLMProviderInterface as Provider,
  ProviderFactoryConfig,
  ProviderRequest,
  ProviderResponse,
  ProviderStreamChunk,
  ModelCapabilities,
  ProviderModelInfo,
  ToolDefinition,
  ToolArgs,
  ToolTenvs,
  InspectedRequest,
} from '@standardagents/spec';
import { ProviderError, defineTool } from '@standardagents/spec';
import { z } from 'zod';
import {
  buildCreateParams,
  transformResponse,
  createStreamState,
  processStreamEvent,
  createErrorChunk,
  getThinkingMode,
  truncateBase64,
  DEFAULT_REASONING_LEVELS,
} from './transformers';
import { getAnthropicIconDataUri } from './icons';

// Re-export ProviderError for consumers
export { ProviderError };

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseRetryAfter(headers: unknown): number | undefined {
  let raw: unknown;
  if (isObjectLike(headers)) {
    const get = headers.get;
    if (typeof get === 'function') {
      raw = get.call(headers, 'retry-after');
    } else {
      raw = headers['retry-after'];
    }
  }
  if (typeof raw === 'number') {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

/**
 * Effort-based reasoning level mapping for adaptive-thinking models.
 */
const ADAPTIVE_REASONING_LEVELS: Record<number, string | null> = DEFAULT_REASONING_LEVELS;

/**
 * Binary reasoning level mapping for budget-based extended thinking models.
 */
const BUDGET_REASONING_LEVELS: Record<number, string | null> = { 0: null, 100: 'enabled' };

/**
 * No reasoning support (Claude 3.x).
 */
const NO_REASONING_LEVELS: Record<number, string | null> = { 0: null };

/**
 * Anthropic provider implementation for Standard Agents
 *
 * Uses the Messages API for all requests via the official @anthropic-ai/sdk.
 * Supports the full Claude family: claude-fable-5, claude-opus-4-x,
 * claude-sonnet-4-x, claude-haiku-4-5, claude-3-x, etc.
 */
export class AnthropicProvider implements Provider {
  readonly name = 'anthropic';
  readonly specificationVersion = '1' as const;

  private client: Anthropic | null = null;
  private config: ProviderFactoryConfig;

  /** Cache for models list to avoid repeated API calls */
  private static modelsCache: Anthropic.Models.ModelInfo[] | null = null;
  private static modelsCacheTime = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(config: ProviderFactoryConfig) {
    this.config = config;
  }

  private async getClient(): Promise<Anthropic> {
    if (!this.client) {
      const { default: Anthropic } = await import('@anthropic-ai/sdk');
      this.client = new Anthropic({
        apiKey: this.config.apiKey,
        baseURL: this.config.baseUrl,
        timeout: this.config.timeout,
      });
    }
    return this.client;
  }

  supportsModel(modelId: string): boolean {
    return modelId.startsWith('claude-');
  }

  /**
   * Get the icon for this provider as a data URI.
   * Always returns the Anthropic icon since all models are from Anthropic.
   */
  getIcon(_modelId?: string): string {
    return getAnthropicIconDataUri();
  }

  // ============================================================================
  // Model Capabilities
  // ============================================================================

  /**
   * Capability mappings for Anthropic models, keyed by model ID prefix.
   * Used as a fallback when the live Models API is unavailable; the API's
   * capability listing takes precedence when reachable.
   */
  private static readonly MODEL_CAPABILITIES: Record<string, ModelCapabilities> = {
    'claude-fable-5': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 128000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-mythos': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 128000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-opus-4-8': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 128000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-opus-4-7': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 128000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-opus-4-6': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 128000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-opus-4-5': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 200000,
      maxOutputTokens: 64000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-opus-4-1': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 32000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-opus-4': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 32000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-sonnet-4-6': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 1000000,
      maxOutputTokens: 64000,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    },
    'claude-sonnet-4-5': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 200000,
      maxOutputTokens: 64000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-sonnet-4': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 64000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-haiku-4-5': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 200000,
      maxOutputTokens: 64000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-3-7-sonnet': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 64000,
      reasoningLevels: BUDGET_REASONING_LEVELS,
    },
    'claude-3-5-sonnet': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 8192,
      reasoningLevels: NO_REASONING_LEVELS,
    },
    'claude-3-5-haiku': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 8192,
      reasoningLevels: NO_REASONING_LEVELS,
    },
    'claude-3-opus': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 4096,
      reasoningLevels: NO_REASONING_LEVELS,
    },
    'claude-3-haiku': {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: false,
      maxContextTokens: 200000,
      maxOutputTokens: 4096,
      reasoningLevels: NO_REASONING_LEVELS,
    },
  };

  /**
   * Look up static capabilities for a model by exact then prefix match.
   */
  private static lookupStaticCapabilities(modelId: string): ModelCapabilities | null {
    if (AnthropicProvider.MODEL_CAPABILITIES[modelId]) {
      return { ...AnthropicProvider.MODEL_CAPABILITIES[modelId] };
    }
    // Longest-prefix match so 'claude-opus-4-5' wins over 'claude-opus-4'
    let best: { prefix: string; caps: ModelCapabilities } | null = null;
    for (const [prefix, caps] of Object.entries(AnthropicProvider.MODEL_CAPABILITIES)) {
      if (modelId.startsWith(prefix) && (!best || prefix.length > best.prefix.length)) {
        best = { prefix, caps };
      }
    }
    return best ? { ...best.caps } : null;
  }

  /**
   * Map a live Models API capability listing to Standard Agents capabilities.
   */
  private static mapLiveCapabilities(model: Anthropic.Models.ModelInfo): ModelCapabilities | null {
    const caps = model.capabilities;
    if (!caps) return null;

    let reasoningLevels: Record<number, string | null>;
    if (caps.thinking.types.adaptive.supported && caps.effort.supported) {
      reasoningLevels = ADAPTIVE_REASONING_LEVELS;
    } else if (caps.thinking.supported) {
      reasoningLevels = BUDGET_REASONING_LEVELS;
    } else {
      reasoningLevels = NO_REASONING_LEVELS;
    }

    return {
      supportsImages: caps.image_input.supported,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: caps.structured_outputs.supported,
      ...(model.max_input_tokens ? { maxContextTokens: model.max_input_tokens } : {}),
      ...(model.max_tokens ? { maxOutputTokens: model.max_tokens } : {}),
      reasoningLevels,
    };
  }

  /**
   * Get capabilities for a specific model.
   * Prefers the live Models API (which reports per-model capability flags)
   * and falls back to the static prefix table when the API is unreachable.
   */
  async getModelCapabilities(modelId: string): Promise<ModelCapabilities | null> {
    try {
      const models = await this.fetchModelsWithCache();
      const model = models.find((m) => m.id === modelId);
      if (model) {
        const live = AnthropicProvider.mapLiveCapabilities(model);
        if (live) {
          return live;
        }
      }
    } catch {
      // Fall through to static lookup
    }

    const staticCaps = AnthropicProvider.lookupStaticCapabilities(modelId);
    if (staticCaps) {
      return staticCaps;
    }

    // Default capabilities for unknown Claude models
    return {
      supportsImages: true,
      supportsToolCalls: true,
      supportsStreaming: true,
      supportsJsonMode: true,
      maxContextTokens: 200000,
      maxOutputTokens: 8192,
      reasoningLevels: ADAPTIVE_REASONING_LEVELS,
    };
  }

  // ============================================================================
  // Provider-Embedded Tools
  // ============================================================================

  /**
   * Provider-embedded tools using defineTool().
   * These are Anthropic's server-side tools that execute on Anthropic's
   * servers. The execute function is a no-op since execution is handled by
   * the provider; results surface as providerTools entries on the response.
   */
  private static readonly TOOLS: Record<string, ToolDefinition<any, ToolArgs | null, ToolTenvs | null>> = {
    web_search: defineTool({
      description: 'Search the web for up-to-date information with citations',
      args: z.object({
        query: z.string().describe('Search query'),
      }),
      execute: async (_state, _args) => {
        // Execution handled by Anthropic - this is a passthrough
        return { status: 'success', result: 'Handled by Anthropic' };
      },
      executionMode: 'provider',
      executionProvider: 'anthropic',
    }),
  };

  /**
   * Get tools embedded in this provider.
   * Anthropic's web_search server tool is available on all current models.
   *
   * @param modelId - Optional filter to get tools available for a specific model
   * @returns Record of tool name to tool definition
   */
  getTools(_modelId?: string): Record<string, ToolDefinition<any, ToolArgs | null, ToolTenvs | null>> {
    return { ...AnthropicProvider.TOOLS };
  }

  // ============================================================================
  // Model Listing
  // ============================================================================

  /**
   * Fetch models from the Anthropic Models API with caching.
   */
  private async fetchModelsWithCache(): Promise<Anthropic.Models.ModelInfo[]> {
    const now = Date.now();

    if (
      AnthropicProvider.modelsCache &&
      now - AnthropicProvider.modelsCacheTime < AnthropicProvider.CACHE_TTL
    ) {
      return AnthropicProvider.modelsCache;
    }

    const client = await this.getClient();
    const models: Anthropic.Models.ModelInfo[] = [];
    for await (const model of client.models.list({ limit: 100 })) {
      models.push(model);
    }

    AnthropicProvider.modelsCache = models;
    AnthropicProvider.modelsCacheTime = now;

    return models;
  }

  /**
   * Map Anthropic model info to ProviderModelInfo.
   */
  private mapToProviderModelInfo(model: Anthropic.Models.ModelInfo): ProviderModelInfo {
    const capabilities =
      AnthropicProvider.mapLiveCapabilities(model) ??
      AnthropicProvider.lookupStaticCapabilities(model.id) ??
      undefined;

    return {
      id: model.id,
      name: model.display_name,
      contextLength: model.max_input_tokens ?? capabilities?.maxContextTokens,
      iconId: this.getIcon(model.id),
      ...(capabilities ? { capabilities } : {}),
    };
  }

  /**
   * Get list of available models from Anthropic.
   * Fetches from the Models API with caching.
   *
   * @param filter - Optional search string to filter models by name/id
   */
  async getModels(filter?: string): Promise<ProviderModelInfo[]> {
    try {
      const rawModels = await this.fetchModelsWithCache();

      let models = rawModels.map((m) => this.mapToProviderModelInfo(m));

      if (filter) {
        const lowerFilter = filter.toLowerCase();
        models = models.filter(
          (m) =>
            m.id.toLowerCase().includes(lowerFilter) ||
            m.name.toLowerCase().includes(lowerFilter)
        );
      }

      return models;
    } catch (error) {
      console.error('Failed to fetch models from Anthropic:', error);
      return [];
    }
  }

  // ============================================================================
  // Generation Methods
  // ============================================================================

  /**
   * Resolve the default max_tokens for a model from the static capability
   * table, clamped so non-streaming requests stay under HTTP timeouts.
   */
  private defaultMaxTokensFor(modelId: string): number {
    const caps = AnthropicProvider.lookupStaticCapabilities(modelId);
    return Math.min(caps?.maxOutputTokens ?? 8192, 8192);
  }

  async generate(request: ProviderRequest): Promise<ProviderResponse> {
    const client = await this.getClient();

    try {
      const params = buildCreateParams(
        request,
        DEFAULT_REASONING_LEVELS,
        this.defaultMaxTokensFor(request.model)
      );

      const response = await client.messages.create(
        { ...params, stream: false },
        { signal: request.signal }
      );

      return transformResponse(response);
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  async stream(request: ProviderRequest): Promise<AsyncIterable<ProviderStreamChunk>> {
    const client = await this.getClient();
    const self = this;

    try {
      const params = buildCreateParams(
        request,
        DEFAULT_REASONING_LEVELS,
        this.defaultMaxTokensFor(request.model)
      );

      const stream = await client.messages.create(
        { ...params, stream: true },
        { signal: request.signal }
      );

      return {
        async *[Symbol.asyncIterator]() {
          const state = createStreamState();

          try {
            for await (const event of stream) {
              const chunks = processStreamEvent(event, state);
              for (const chunk of chunks) {
                yield chunk;
              }
            }
          } catch (error) {
            const providerError = self.toProviderError(error);
            yield createErrorChunk(providerError.message, providerError.code);
          }
        },
      };
    } catch (error) {
      throw this.toProviderError(error);
    }
  }

  // ============================================================================
  // Error Handling
  // ============================================================================

  private toProviderError(error: unknown): ProviderError {
    if (error instanceof ProviderError) {
      return error;
    }

    const record = isObjectLike(error) ? error : null;
    const status =
      typeof record?.status === 'number'
        ? record.status
        : typeof record?.statusCode === 'number'
          ? record.statusCode
          : undefined;
    const message = error instanceof Error ? error.message : String(error);

    if (status === 429) {
      return new ProviderError(message, 'rate_limit', status, parseRetryAfter(record?.headers));
    }
    if (status === 401 || status === 403) {
      return new ProviderError(message, 'auth_error', status);
    }
    if (status === 400 || status === 404 || status === 413) {
      return new ProviderError(message, 'invalid_request', status);
    }
    if (status !== undefined && status >= 500) {
      // Includes 529 overloaded_error - retryable server-side condition
      return new ProviderError(message, 'server_error', status);
    }
    if (
      (error instanceof Error && error.name === 'AbortError') ||
      record?.code === 'ETIMEDOUT'
    ) {
      return new ProviderError(message, 'timeout');
    }

    return new ProviderError(message, 'unknown', status);
  }

  // ============================================================================
  // Inspection
  // ============================================================================

  /**
   * Transform a ProviderRequest to Anthropic Messages API format for inspection.
   * Returns the exact request body that would be sent, with base64 data truncated.
   */
  async inspectRequest(request: ProviderRequest): Promise<InspectedRequest> {
    const params = buildCreateParams(
      request,
      DEFAULT_REASONING_LEVELS,
      this.defaultMaxTokensFor(request.model)
    );

    // Round-trip through JSON to get a plain serializable body for the UI
    const body: Record<string, unknown> = JSON.parse(JSON.stringify(params));

    return {
      body: truncateBase64(body),
      messagesPath: 'messages',
      metadata: {
        endpoint: 'messages.create',
      },
    };
  }
}

// Re-export for capability-aware consumers
export { getThinkingMode };
